import React, { useState, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send } from "lucide-react";
import { API } from "@/lib/api";

export default function AiTeacher({ chapterId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = input.trim();
    setMessages(m => [...m, { role: "user", content: userMsg }]);
    setInput("");
    setStreaming(true);
    setMessages(m => [...m, { role: "assistant", content: "" }]);

    try {
      const token = localStorage.getItem("tpa_token");
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ message: userMsg, chapter_id: chapterId }),
      });

      if (!res.ok || !res.body) {
        throw new Error("Chat request failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const part of parts) {
          if (part.startsWith("data: ")) {
            const chunk = part.slice(6);
            if (chunk === "[DONE]") continue;
            if (chunk.startsWith("[ERROR]")) {
              setMessages(m => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: "I hit a snag answering that. Please try again." };
                return c;
              });
              continue;
            }
            setMessages(m => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: (c[c.length - 1].content || "") + chunk };
              return c;
            });
          }
        }
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 0);
      }
    } catch (e) {
      setMessages(m => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button data-testid="open-ai-teacher-btn" className="fixed right-4 bottom-24 lg:bottom-6 rounded-full shadow-xl h-14 px-5 gap-2 z-30" size="lg">
          <Sparkles size={18} /> Ask AI Teacher
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-border">
          <SheetTitle className="font-serif text-2xl">AI Tax Teacher</SheetTitle>
          <p className="text-sm text-muted-foreground">Ask about the current chapter or any tax topic.</p>
        </SheetHeader>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-sm text-muted-foreground italic">
              Try: "What's the difference between AOTC and LLC?"
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`ai-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={`inline-block max-w-[85%] px-4 py-3 rounded-lg text-sm whitespace-pre-wrap ${
                m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
              }`}>
                {m.content || (streaming ? "…" : "")}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border flex gap-2">
          <Input
            data-testid="ai-teacher-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask a tax question…"
            disabled={streaming}
          />
          <Button data-testid="ai-teacher-send-btn" onClick={send} disabled={streaming || !input.trim()} size="icon"><Send size={16} /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
